import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../components/ui/toast/use-toast';
import {
  KeyRound, Phone, Mail, Search, Moon, Sun, Monitor,
  AlertCircle, Building2, Home, MapPin, Check, X, 
  LogOut, CreditCard, Star, Shield, ChevronRight,
  User, BellRing, CreditCard as PaymentCard, Settings,
  UserCog, LockKeyhole, Bell, Building, Info, Ban,
  Trash2, Plus, CheckCircle, AlertTriangle, Clock,
  Eye, EyeOff, Palette, Smartphone, Globe, Activity,
  RefreshCw, TrendingUp, Save, Zap, Sparkles, Calendar
} from 'lucide-react';
import payuService from '../services/payuService';

// Componente para el header mejorado
const PageHeader = ({ icon: Icon, title, subtitle, actionButton }) => (
  <div className="mb-8">
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
      <div className="flex items-center gap-4 mb-4 lg:mb-0">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl shadow-lg">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{title}</h1>
          {subtitle && <p className="text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
      </div>
      {actionButton}
    </div>
  </div>
);

// Componente para tarjetas de los distintos grupos de ajustes
const SettingsGroupCard = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 hover:shadow-lg ${className}`}>
    <div className="border-b border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center gap-3">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-3 rounded-xl">
          <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          {title}
        </h3>
      </div>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

// Componente para cada opción de configuración mejorado
const SettingOption = ({ 
  icon: Icon, 
  title, 
  description, 
  onClick, 
  disabled = false, 
  renderDescription,
  badge,
  variant = 'default'
}) => {
  const variants = {
    default: 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/10',
    success: 'border-green-200 dark:border-green-700 hover:border-green-300 dark:hover:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/10',
    warning: 'border-amber-200 dark:border-amber-700 hover:border-amber-300 dark:hover:border-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/10',
    danger: 'border-red-200 dark:border-red-700 hover:border-red-300 dark:hover:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/10'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left p-5 rounded-xl transition-all duration-300 border-2
                ${variants[variant]}
                hover:shadow-md hover:-translate-y-0.5 transform
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <div className="flex items-start gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-3 rounded-xl flex-shrink-0">
          <Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
            {badge && (
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${badge.className}`}>
                {badge.text}
              </span>
            )}
          </div>
          {typeof renderDescription === 'function' 
            ? renderDescription() 
            : <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">{description}</p>
          }
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" />
      </div>
    </button>
  );
};

// Componente para el modal mejorado
const Modal = ({ isOpen, onClose, title, children, size = 'default' }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    default: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl ${sizes[size]} w-full p-6 transform transition-all duration-300 scale-100 animate-scaleIn`}>
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Componente Input mejorado para formularios
const FormInput = ({ 
  type = "text", 
  label, 
  placeholder, 
  value, 
  onChange, 
  icon: Icon, 
  error,
  showPassword,
  onTogglePassword,
  className = ""
}) => (
  <div className="space-y-2">
    {label && (
      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
        {label}
      </label>
    )}
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
      )}
      <input
        type={type === "password" && showPassword ? "text" : type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full p-3 ${Icon ? 'pl-11' : 'pl-3'} ${type === "password" ? 'pr-11' : 'pr-3'} 
                  border-2 rounded-xl transition-all duration-200
                  focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                  ${error ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' : 'border-gray-200 dark:border-gray-600'}
                  dark:bg-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-400
                  ${className}`}
      />
      {type === "password" && (
        <button
          type="button"
          onClick={onTogglePassword}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          ) : (
            <Eye className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      )}
    </div>
    {error && (
      <p className="text-sm text-red-500 flex items-center gap-1">
        <AlertCircle className="w-4 h-4" />
        {error}
      </p>
    )}
  </div>
);

// Componente de perfil de usuario mejorado
const UserProfileSummary = ({ user, planInfo, planLoading, planNames, planColors, logout, navigate }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden sticky top-24">
    <div className="p-6 text-center bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
      <div className="w-24 h-24 mx-auto bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full p-1 mb-4">
        <div className="w-full h-full rounded-full overflow-hidden bg-white dark:bg-gray-700 flex items-center justify-center">
          {user?.avatar ? (
            <img 
              src={user.avatar} 
              alt={user?.nombre || "Usuario"} 
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-12 h-12 text-gray-400" />
          )}
        </div>
      </div>
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1">
        {user?.nombre || "Usuario"}
      </h3>
      <p className="text-gray-500 dark:text-gray-400 text-sm">
        {user?.email || "email@example.com"}
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-xs mb-4">
        ID: {user?.id || "N/A"}
      </p>
      
      {/* Plan Badge */}
      {user?.tipo_usuario_id !== 1 && planInfo && (
        <div className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4
                      ${planColors[planInfo.plan || 'gratuito']}`}>
          <Star className="w-4 h-4 inline mr-1" />
          Plan {planNames[planInfo.plan || 'gratuito']}
        </div>
      )}
      
      {/* User Type */}
      <div className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        <span className="font-semibold px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
          {user?.tipo_usuario_id === 1 ? 'Cliente' :
            user?.tipo_usuario_id === 2 ? 'Vendedor' :
            user?.tipo_usuario_id === 3 ? 'Constructora' :
            user?.tipo_usuario_id === 4 ? 'Inmobiliaria' : 'Usuario'}
        </span>
      </div>
      
      {/* Account Status */}
      <div className="flex justify-center space-x-4 mb-6">
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-3 h-3 rounded-full ${user?.email_verificado ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <span className={user?.email_verificado ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>Email</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-3 h-3 rounded-full ${user?.telefono_verificado ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <span className={user?.telefono_verificado ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>Teléfono</span>
        </div>
      </div>
      
      {/* Logout Button */}
      <Button
        variant="danger"
        onClick={() => {
          logout();
          navigate('/login');
        }}
        icon={LogOut}
        className="w-full"
      >
        Cerrar Sesión
      </Button>
    </div>
  </div>
);

// Componente para la tarjeta guardada mejorado
const SavedCardItem = ({ card, onSetPrimary, onDelete, isPrimary }) => (
  <div className={`p-4 border-2 rounded-xl transition-all duration-200 ${
    isPrimary 
      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
  }`}>
    <div className="flex justify-between">
      <div className="flex items-center">
        <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 p-3 rounded-xl">
          <CreditCard className="w-6 h-6 text-gray-600 dark:text-gray-400" />
        </div>
        <div className="ml-3">
          <p className="font-semibold text-gray-800 dark:text-gray-200">
            •••• {card.lastFour}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Exp: {card.expiryMonth}/{card.expiryYear}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isPrimary ? (
          <div className="flex items-center text-blue-600 dark:text-blue-400 text-sm font-semibold">
            <CheckCircle className="w-4 h-4 mr-1" />
            Principal
          </div>
        ) : (
          <button 
            onClick={() => onSetPrimary(card.id)}
            className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
          >
            Establecer principal
          </button>
        )}
        <button
          onClick={() => onDelete(card)}
          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

// Componente Button reutilizable mejorado
const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  disabled = false, 
  className = '',
  icon: Icon,
  isLoading = false,
  size = 'default'
}) => {
  const variants = {
    primary: 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
    danger: 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-lg',
    outline: 'bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-200 hover:border-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
    success: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    default: 'px-4 py-2.5',
    lg: 'px-6 py-3 text-lg'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`rounded-xl font-medium transition-all duration-300 transform hover:scale-105
                flex items-center justify-center gap-2
                ${variants[variant]} 
                ${sizes[size]}
                ${disabled || isLoading ? 'opacity-50 cursor-not-allowed transform-none' : ''}
                ${className}`}
    >
      {isLoading && (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {Icon && !isLoading && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

// Componente para selector de tema
const ThemeSelector = ({ currentTheme, onThemeChange }) => {
  const themes = [
    { id: 'light', label: 'Claro', icon: Sun, description: 'Interfaz con colores claros' },
    { id: 'dark', label: 'Oscuro', icon: Moon, description: 'Interfaz con colores oscuros' },
    { id: 'system', label: 'Sistema', icon: Monitor, description: 'Sigue la configuración del sistema' }
  ];

  return (
    <div className="space-y-3">
      {themes.map((theme) => (
        <button
          key={theme.id}
          onClick={() => onThemeChange(theme.id)}
          className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left
                    ${currentTheme === theme.id 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                    }`}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              currentTheme === theme.id 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
            }`}>
              <theme.icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-800 dark:text-gray-200">{theme.label}</h4>
                {currentTheme === theme.id && (
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{theme.description}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

const Configuracion = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { theme, setTheme } = useTheme();
  
  // Estados
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
  const [verificationStep, setVerificationStep] = useState('initial');
  const [verificationCode, setVerificationCode] = useState('');
  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(theme);
  const [activeSettingsTab, setActiveSettingsTab] = useState('account');
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [reactivateSubscriptionLoading, setReactivateSubscriptionLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para métodos de pago
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(true);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [newCardData, setNewCardData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    docNumber: ''
  });
  const [processingCard, setProcessingCard] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  
  // Preferencias de búsqueda
  const [preferences, setPreferences] = useState({
    tipo_propiedad: [],
    presupuesto_min: '',
    presupuesto_max: '',
    dormitorios: '',
    banos: '',
    area_min: '',
    area_max: ''
  });

  // Estados para información del plan
  const [planInfo, setPlanInfo] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState(false);

  // Mapeo de nombres de planes
  const planNames = {
    gratuito: 'Gratuito',
    casual: 'Casual',
    basico: 'Básico',
    estandar: 'Estándar',
    premium: 'Premium'
  };

  // Colores de planes
  const planColors = {
    gratuito: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    casual: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    basico: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    estandar: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    premium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
  };

  // Detector de tamaño de ventana para responsividad
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Cargar métodos de pago guardados
  const loadSavedPaymentMethods = async () => {
    if (!isAuthenticated) return;
    
    try {
      setPaymentMethodsLoading(true);
      const response = await payuService.getSavedPaymentMethods();
      
      if (response.success) {
        setSavedPaymentMethods(response.paymentMethods || []);
      }
    } catch (error) {
      console.error('Error al cargar métodos de pago:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los métodos de pago guardados",
        variant: "destructive"
      });
    } finally {
      setPaymentMethodsLoading(false);
    }
  };
  
  // Cargar historial de pagos
  const loadPaymentHistory = async () => {
    if (!isAuthenticated) return;
    
    try {
      setPaymentMethodsLoading(true);
      const response = await payuService.getPaymentHistory();
      
      if (response.success) {
        setPaymentHistory(response.history || []);
      }
    } catch (error) {
      console.error('Error al cargar historial de pagos:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar el historial de pagos",
        variant: "destructive"
      });
    } finally {
      setPaymentMethodsLoading(false);
    }
  };
  
  // Función para guardar una nueva tarjeta
  const handleSaveCard = async () => {
    try {
      setProcessingCard(true);
      
      // Validar datos de la tarjeta
      if (!newCardData.cardNumber || !newCardData.cardHolder || 
          !newCardData.expiryMonth || !newCardData.expiryYear || !newCardData.cvv) {
        throw new Error('Por favor completa todos los campos');
      }
      
      const result = await payuService.savePaymentMethod(newCardData);
      
      if (result.success) {
        toast({
          title: "Tarjeta guardada",
          description: "La tarjeta ha sido registrada exitosamente",
          variant: "success"
        });
        
        // Recargar métodos de pago
        await loadSavedPaymentMethods();
        setIsAddCardModalOpen(false);
        
        // Reiniciar formulario
        setNewCardData({
          cardNumber: '',
          cardHolder: '',
          expiryMonth: '',
          expiryYear: '',
          cvv: '',
          docNumber: ''
        });
      }
    } catch (error) {
      console.error('Error al guardar tarjeta:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la tarjeta",
        variant: "destructive"
      });
    } finally {
      setProcessingCard(false);
    }
  };
  
  // Función para eliminar una tarjeta
  const handleDeleteCard = async () => {
    if (!selectedCard) return;
    
    try {
      setProcessingCard(true);
      const result = await payuService.deletePaymentMethod(selectedCard.id);
      
      if (result.success) {
        toast({
          title: "Tarjeta eliminada",
          description: "La tarjeta ha sido eliminada exitosamente",
          variant: "success"
        });
        
        // Recargar métodos de pago
        await loadSavedPaymentMethods();
        setIsDeleteCardModalOpen(false);
        setSelectedCard(null);
      }
    } catch (error) {
      console.error('Error al eliminar tarjeta:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar la tarjeta",
        variant: "destructive"
      });
    } finally {
      setProcessingCard(false);
    }
  };
  
  // Función para establecer como principal
  const handleSetPrimary = async (cardId) => {
    try {
      setPaymentMethodsLoading(true);
      const result = await payuService.setPrimaryPaymentMethod(cardId);
      
      if (result.success) {
        toast({
          title: "Tarjeta principal",
          description: "Se ha establecido como método de pago principal",
          variant: "success"
        });
        
        // Recargar métodos de pago
        await loadSavedPaymentMethods();
      }
    } catch (error) {
      console.error('Error al establecer tarjeta principal:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo establecer como principal",
        variant: "destructive"
      });
    } finally {
      setPaymentMethodsLoading(false);
    }
  };
  
  // Función para formatear el número de tarjeta
  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 16) value = value.slice(0, 16);
    
    // Formatear con espacios cada 4 dígitos
    value = value.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
    
    setNewCardData({
      ...newCardData,
      cardNumber: value
    });
  };

  // Determinar si estamos en modo móvil
  const isMobile = windowWidth < 768;

  // Método para obtener información del plan
  const fetchPlanInfo = async () => {
    if (!isAuthenticated) return;
    
    try {
      setPlanLoading(true);
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/plan-info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al obtener información del plan');
      }

      const data = await response.json();
      setPlanInfo(data);
    } catch (error) {
      console.error('Error fetching plan info:', error);
      toast({
        title: "Error",
        description: "No se pudo cargar la información del plan",
        variant: "destructive"
      });
    } finally {
      setPlanLoading(false);
    }
  };

  // Efecto para obtener información del plan y métodos de pago
  useEffect(() => {
    if (isAuthenticated) {
      fetchPlanInfo();
      
      if (activeSettingsTab === 'subscription') {
        loadSavedPaymentMethods();
        loadPaymentHistory();
      }
    }
  }, [isAuthenticated, activeSettingsTab]);

  // Timer para reenvío de código
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setResendDisabled(false);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft]);

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Renderizar descripción del plan
  const renderPlanDescription = () => {
    if (planLoading) return "Cargando información del plan...";
    
    if (!planInfo) return "Plan no disponible";
    
    const currentPlan = planInfo.plan || 'gratuito';
    const totalProperties = planInfo.total_propiedades || 0;
    
    // Definir límite según el tipo de usuario y plan
    const limit = user?.tipo_usuario_id === 1 ? 1 :
                 user?.tipo_usuario_id === 2 ? 
                   (currentPlan === 'casual' ? 5 : 1) :
                 user?.tipo_usuario_id === 3 ?
                   (currentPlan === 'basico' ? 15 : 
                    currentPlan === 'estandar' ? 30 : 
                    currentPlan === 'premium' ? Infinity : 1) :
                 user?.tipo_usuario_id === 4 ? 
                   (currentPlan === 'basico' ? 10 : 
                    currentPlan === 'estandar' ? 20 : 
                    currentPlan === 'premium' ? Infinity : 0) :
                 0;
  
    // Formatear fecha de próximo pago
    const proximoPago = planInfo.fecha_proximo_pago 
      ? new Date(planInfo.fecha_proximo_pago).toLocaleDateString('es-PE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
      : 'No disponible';
    
    // Verificar renovación automática
    const renovacionAutomatica = planInfo.renovacion_automatica === 'si';
  
    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600 dark:text-gray-400">Publicaciones:</span>
          <span 
            className={`px-3 py-1 rounded-full text-xs font-semibold ${planColors[currentPlan]}`}
          >
            {totalProperties} / {limit === Infinity ? '∞' : limit}
          </span>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Próximo cobro: <span className="font-semibold">{proximoPago}</span>
        </div>
        {renovacionAutomatica ? (
          <div className="mt-2 text-green-600 dark:text-green-400 text-sm flex items-center gap-1">
            <CheckCircle className="w-4 h-4" />
            Renovación automática activada
          </div>
        ) : (
          <div className="mt-2 text-amber-600 dark:text-amber-400 text-sm flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            Renovación automática desactivada
          </div>
        )}
      </div>
    );
  };

  const handleReactivateSubscription = async () => {
    try {
      setReactivateSubscriptionLoading(true);
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/reactivate-plan-renewal', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || 'Error al reactivar renovación');
      }
  
      toast({
        title: "Renovación reactivada",
        description: "La renovación automática ha sido activada nuevamente",
        variant: "success"
      });
  
      // Recargar información del plan
      await fetchPlanInfo();
      setIsSubscriptionModalOpen(false);
    } catch (error) {
      console.error('Error reactivando renovación:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo reactivar la renovación",
        variant: "destructive"
      });
    } finally {
      setReactivateSubscriptionLoading(false);
    }
  };

  // Función para cancelar la renovación automática
  const handleCancelSubscription = async () => {
    try {
      setCancelSubscriptionLoading(true);
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/cancel-plan-renewal', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || 'Error al cancelar renovación');
      }
  
      toast({
        title: "Renovación cancelada",
        description: "La renovación automática ha sido desactivada",
        variant: "success"
      });
  
      // Recargar información del plan
      await fetchPlanInfo();
      setIsSubscriptionModalOpen(false);
    } catch (error) {
      console.error('Error cancelando renovación:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cancelar la renovación",
        variant: "destructive"
      });
    } finally {
      setCancelSubscriptionLoading(false);
    }
  };

  // Manejadores para cambios de configuración
  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/password-reset-email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          email: user.email,
          userId: user.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar código de verificación');
      }

      setVerificationStep('verify');
      toast({
        title: "Código enviado",
        description: "Por favor revisa tu correo electrónico",
        variant: "success"
      });
    } catch (error) {
      console.error('Error en cambio de contraseña:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al enviar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/phone-verification-email', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          email: user.email,
          userId: user.id,
          currentPhone: user.telefono
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar código de verificación');
      }

      setVerificationStep('verify');
      toast({
        title: "Código enviado",
        description: "Por favor revisa tu correo electrónico",
        variant: "success"
      });
    } catch (error) {
      console.error('Error en cambio de teléfono:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al enviar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!user.telefono) {
        throw new Error('Necesitas tener un número de teléfono verificado para cambiar tu email');
      }

      if (!newValue || !newValue.includes('@')) {
        throw new Error('Por favor ingresa un email válido');
      }

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/email-verification-sms', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          userId: user.id,
          phone: user.telefono,
          prefijo: user.prefijo_telefono || '+51',
          currentEmail: user.email,
          newEmail: newValue
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al enviar código de verificación');
      }

      const data = await response.json();

      // Establecer tiempo de espera para reenvío (10 minutos)
      setTimeLeft(600);
      setResendDisabled(true);

      setVerificationStep('verify');
      toast({
        title: "Código enviado",
        description: "Por favor revisa tus mensajes SMS",
        variant: "success"
      });
    } catch (error) {
      console.error('Error en cambio de email:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al enviar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Primero verificamos el código SMS
      const verifyResponse = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/verify-sms', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          telefono: user.telefono,
          prefijo: user.prefijo_telefono || '+51',
          code: verificationCode
        })
      });

      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.message || 'El código es inválido o ha expirado');
      }

      const verifyData = await verifyResponse.json();
      
      if (!verifyData.success) {
        throw new Error(verifyData.error || verifyData.message || 'Error al verificar el código');
      }
      
      // Si la verificación del código SMS es exitosa, actualizamos el email
      const updateResponse = await fetch('https://backend-production-1786.up.railway.app/api/auth/update-email-direct', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          newEmail: newValue
        })
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.message || 'Error al actualizar el email');
      }

      toast({
        title: "Email actualizado",
        description: "Tu correo electrónico ha sido actualizado exitosamente",
        variant: "success"
      });

      setIsEmailModalOpen(false);
      setVerificationStep('initial');
      setVerificationCode('');
      setNewValue('');
      
      // Recargar la página para reflejar los cambios
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error en verificación de email:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al verificar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejadores de verificación de códigos
  const handleVerifyPasswordCode = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verify-password-reset', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          code: verificationCode,
          newPassword: newValue
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al verificar el código');
      }

      toast({
        title: "Contraseña actualizada",
        description: "Tu contraseña ha sido cambiada exitosamente",
        variant: "success"
      });

      setIsPasswordModalOpen(false);
      setVerificationStep('initial');
      setVerificationCode('');
      setNewValue('');
    } catch (error) {
      console.error('Error en verificación de contraseña:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al verificar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verify-phone-change', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: user.id,
          code: verificationCode,
          newPhone: newValue
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al verificar el código');
      }

      toast({
        title: "Teléfono actualizado",
        description: "Tu número de teléfono ha sido actualizado exitosamente",
        variant: "success"
      });

      setIsPhoneModalOpen(false);
      setVerificationStep('initial');
      setVerificationCode('');
      setNewValue('');
    } catch (error) {
      console.error('Error en verificación de teléfono:', error);
      toast({
        title: "Error",
        description: error.message || "Hubo un error al verificar el código",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesChange = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://backend-production-1786.up.railway.app/api/user/preferences', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) throw new Error('Error al actualizar preferencias');

      toast({
        title: "Preferencias actualizadas",
        description: "Tus preferencias han sido guardadas exitosamente",
        variant: "success"
      });
      
      setIsPreferencesModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyTypeToggle = (tipo) => {
    setPreferences(prev => ({
      ...prev,
      tipo_propiedad: prev.tipo_propiedad.includes(tipo)
        ? prev.tipo_propiedad.filter(t => t !== tipo)
        : [...prev.tipo_propiedad, tipo]
    }));
  };

  // Función para manejar cambio de tema
  const handleThemeChange = (newTheme) => {
    setSelectedTheme(newTheme);
    setTheme(newTheme);
    
    toast({
      title: "Tema actualizado",
      description: `Se ha cambiado al modo ${
        newTheme === 'light' ? 'claro' : 
        newTheme === 'dark' ? 'oscuro' : 
        'del sistema'
      }`,
      variant: "success"
    });
    
    setIsThemeModalOpen(false);
  };

  // Pestañas de navegación
  const tabs = [
    { id: 'account', label: 'Cuenta', icon: UserCog },
    { id: 'security', label: 'Seguridad', icon: LockKeyhole },
    { id: 'preferences', label: 'Preferencias', icon: Settings },
    { id: 'subscription', label: 'Suscripción', icon: PaymentCard }
  ];

  // Función para mostrar u ocultar el perfil según la pestaña activa y el tamaño de pantalla
  const shouldShowProfile = () => {
    if (!isMobile) return true;
    return activeSettingsTab === 'account';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Settings}
          title="Configuración"
          subtitle="Gestiona tu cuenta, seguridad y preferencias de la plataforma"
          actionButton={
            <Button
              icon={RefreshCw}
              onClick={() => window.location.reload()}
              variant="secondary"
            >
              Actualizar
            </Button>
          }
        />

        {/* Navigation Tabs */}
        <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-2">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSettingsTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-300
                          whitespace-nowrap min-w-fit
                          ${activeSettingsTab === tab.id 
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105' 
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar - User Profile Summary */}
          {shouldShowProfile() && (
            <div className="lg:col-span-1">
              <UserProfileSummary 
                user={user} 
                planInfo={planInfo} 
                planLoading={planLoading} 
                planNames={planNames} 
                planColors={planColors} 
                logout={logout} 
                navigate={navigate} 
              />
            </div>
          )}
          
          {/* Main Content */}
          <div className={isMobile && !shouldShowProfile() ? "col-span-1" : "lg:col-span-2"}>
            <div className="space-y-8">
              {/* Account Settings Tab */}
              {activeSettingsTab === 'account' && (
                <>
                  <SettingsGroupCard title="Información Personal" icon={User}>
                    <div className="space-y-4">
                      <SettingOption 
                        icon={Mail}
                        title="Cambiar Email"
                        description={`Email actual: ${user?.email || 'No disponible'}`}
                        onClick={() => setIsEmailModalOpen(true)}
                        variant="default"
                      />
                      <SettingOption 
                        icon={Phone}
                        title="Cambiar Número de Teléfono"
                        description={`Teléfono actual: ${user?.telefono || 'No disponible'}`}
                        onClick={() => setIsPhoneModalOpen(true)}
                        variant="default"
                      />
                    </div>
                  </SettingsGroupCard>

                  {/* Upgrade Options */}
                  {user?.tipo_usuario_id === 1 && (
                    <SettingsGroupCard title="Oportunidades de Crecimiento" icon={Star} className="border-amber-200 dark:border-amber-700">
                      <SettingOption 
                        icon={Building}
                        title="Conviértete en Vendedor"
                        description="Expande tus oportunidades en el mercado inmobiliario y comienza a publicar propiedades."
                        onClick={() => navigate('/upgrade-user')}
                        variant="success"
                        badge={{
                          text: 'Recomendado',
                          className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }}
                      />
                    </SettingsGroupCard>
                  )}

                  {user?.tipo_usuario_id === 2 && (
                    <SettingsGroupCard title="Oportunidades de Crecimiento" icon={Star} className="border-purple-200 dark:border-purple-700">
                      <SettingOption 
                        icon={Building2}
                        title="Conviértete en Socio Inmobiliaria"
                        description="Lleva tu negocio inmobiliario al siguiente nivel con herramientas avanzadas y exposición prioritaria."
                        onClick={() => navigate('/upgrade-to-inmobiliaria')}
                        variant="success"
                        badge={{
                          text: 'Premium',
                          className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                        }}
                      />
                    </SettingsGroupCard>
                  )}
                </>
              )}

              {/* Security Settings Tab */}
              {activeSettingsTab === 'security' && (
                <SettingsGroupCard title="Seguridad de la Cuenta" icon={Shield}>
                  <div className="space-y-4">
                    <SettingOption 
                      icon={KeyRound}
                      title="Cambiar Contraseña"
                      description="Actualiza tu contraseña y cierra sesión en todos los dispositivos"
                      onClick={() => setIsPasswordModalOpen(true)}
                      variant="warning"
                    />
                  </div>
                </SettingsGroupCard>
              )}

              {/* Preferences Tab */}
              {activeSettingsTab === 'preferences' && (
                <>
                  <SettingsGroupCard title="Preferencias de Búsqueda" icon={Search}>
                    <SettingOption 
                      icon={Search}
                      title="Criterios de Búsqueda"
                      description="Personaliza tus criterios de búsqueda de propiedades"
                      onClick={() => setIsPreferencesModalOpen(true)}
                      variant="default"
                    />
                  </SettingsGroupCard>
                  
                  <SettingsGroupCard title="Apariencia" icon={Palette}>
                    <SettingOption 
                      icon={theme === 'dark' ? Moon : theme === 'light' ? Sun : Monitor}
                      title="Tema de la Interfaz"
                      renderDescription={() => (
                        <div className="space-y-1">
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            Modo actual: <span className="font-semibold capitalize">
                              {theme === 'light' ? 'Claro' : 
                               theme === 'dark' ? 'Oscuro' : 
                               'Sistema'}
                            </span>
                          </p>
                          <p className="text-gray-500 dark:text-gray-500 text-xs">
                            Personaliza la apariencia de la interfaz
                          </p>
                        </div>
                      )}
                      onClick={() => setIsThemeModalOpen(true)}
                      variant="default"
                      badge={{
                        text: theme === 'light' ? 'Claro' : theme === 'dark' ? 'Oscuro' : 'Sistema',
                        className: `${
                          theme === 'light' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                          theme === 'dark' ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`
                      }}
                    />
                  </SettingsGroupCard>
                </>
              )}

              {/* Subscription Tab */}
              {activeSettingsTab === 'subscription' && (
                <>
                  {user?.tipo_usuario_id !== 1 ? (
                    <>
                      <SettingsGroupCard title="Plan Actual" icon={CreditCard}>
                        <SettingOption
                          icon={PaymentCard}
                          title={`Plan ${planNames[planInfo?.plan || 'gratuito']}`}
                          renderDescription={renderPlanDescription}
                          onClick={() => setIsSubscriptionModalOpen(true)}
                          badge={{
                            text: planInfo?.plan ? planNames[planInfo.plan] : 'Gratuito',
                            className: planColors[planInfo?.plan || 'gratuito']
                          }}
                          variant="default"
                        />
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                          <Button
                            variant="secondary"
                            icon={Info}
                            onClick={() => setIsSubscriptionModalOpen(true)}
                            className="flex-1"
                          >
                            Ver Detalles
                          </Button>
                          <Button
                            variant="primary"
                            icon={TrendingUp}
                            onClick={() => navigate('/upgrade-plan')}
                            className="flex-1"
                          >
                            Cambiar Plan
                          </Button>
                        </div>
                      </SettingsGroupCard>

                      <SettingsGroupCard title="Métodos de Pago" icon={CreditCard}>
                        <div className="space-y-4">
                          {paymentMethodsLoading ? (
                            <div className="text-center py-8">
                              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                              <p className="text-gray-600 dark:text-gray-400 text-sm">Cargando métodos de pago...</p>
                            </div>
                          ) : savedPaymentMethods.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 p-4 inline-flex rounded-2xl mb-4">
                                <CreditCard className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                              </div>
                              <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                No tienes métodos de pago
                              </h4>
                              <p className="text-gray-600 dark:text-gray-400 mb-6">
                                Añade una tarjeta para facilitar tus pagos futuros
                              </p>
                              <Button 
                                onClick={() => setIsAddCardModalOpen(true)}
                                variant="primary"
                                icon={Plus}
                              >
                                Añadir Primera Tarjeta
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="space-y-3">
                                {savedPaymentMethods.map((card, index) => (
                                  <SavedCardItem 
                                    key={card.id}
                                    card={card}
                                    isPrimary={index === 0}
                                    onSetPrimary={handleSetPrimary}
                                    onDelete={(card) => {
                                      setSelectedCard(card);
                                      setIsDeleteCardModalOpen(true);
                                    }}
                                  />
                                ))}
                              </div>
                              <Button 
                                onClick={() => setIsAddCardModalOpen(true)}
                                variant="outline"
                                icon={Plus}
                                className="w-full mt-4"
                              >
                                Añadir Nueva Tarjeta
                              </Button>
                            </>
                          )}
                        </div>
                      </SettingsGroupCard>

                      <SettingsGroupCard title="Historial de Pagos" icon={Clock}>
                        <div className="space-y-4">
                          {paymentMethodsLoading ? (
                            <div className="text-center py-8">
                              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                              <p className="text-gray-600 dark:text-gray-400 text-sm">Cargando historial...</p>
                            </div>
                          ) : (
                            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                  <thead className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700">
                                    <tr>
                                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Plan</th>
                                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Monto</th>
                                      <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {paymentHistory?.length > 0 ? paymentHistory.map((payment, idx) => (
                                      <tr key={idx} className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                        idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'
                                      }`}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                          {new Date(payment.fecha_creacion).toLocaleDateString('es-PE')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                          {payment.plan_nombre}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">
                                          S/ {parseFloat(payment.monto).toFixed(2)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                            ${payment.estado === 'APROBADO' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 
                                              payment.estado === 'PENDIENTE' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : 
                                              'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                                            {payment.estado}
                                          </span>
                                        </td>
                                      </tr>
                                    )) : (
                                      <tr>
                                        <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                          <div className="flex flex-col items-center">
                                            <Clock className="w-8 h-8 text-gray-400 mb-2" />
                                            <p>No hay historial de pagos disponible</p>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </SettingsGroupCard>
                    </>
                  ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
                      <div className="mb-6">
                        <div className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 p-4 rounded-2xl inline-flex">
                          <Building2 className="w-12 h-12 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-3">
                        Sin Plan Activo
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
                        Como cliente, no tienes un plan activo. Actualiza a vendedor para publicar propiedades y acceder a funciones avanzadas.
                      </p>
                      <Button
                        onClick={() => navigate('/upgrade-user')}
                        icon={Sparkles}
                        variant="primary"
                        size="lg"
                      >
                        Convertirme en Vendedor
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modales */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setVerificationStep('initial');
          setVerificationCode('');
          setNewValue('');
          setShowPassword(false);
        }}
        title="Cambiar Contraseña"
      >
        {verificationStep === 'initial' ? (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-blue-800 dark:text-blue-300 flex items-start">
                <Mail className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Te enviaremos un código de verificación a tu correo electrónico <strong>{user.email}</strong>.</span>
              </p>
            </div>
            <Button
              onClick={handlePasswordChange}
              isLoading={loading}
              className="w-full"
              icon={KeyRound}
            >
              Enviar Código de Verificación
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <FormInput
              type="text"
              label="Código de verificación"
              placeholder="Ingresa el código de 6 dígitos"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              icon={KeyRound}
            />
            <FormInput
              type="password"
              label="Nueva contraseña"
              placeholder="Ingresa tu nueva contraseña"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              icon={LockKeyhole}
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
            />
            <Button
              onClick={handleVerifyPasswordCode}
              isLoading={loading}
              disabled={!verificationCode || !newValue}
              className="w-full"
              icon={Check}
            >
              Cambiar Contraseña
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isPhoneModalOpen}
        onClose={() => {
          setIsPhoneModalOpen(false);
          setVerificationStep('initial');
          setVerificationCode('');
          setNewValue('');
        }}
        title="Cambiar Número de Teléfono"
      >
        {verificationStep === 'initial' ? (
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-blue-800 dark:text-blue-300 flex items-start">
                <Mail className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Te enviaremos un código de verificación a tu correo electrónico <strong>{user.email}</strong>.</span>
              </p>
            </div>
            <Button
              onClick={handlePhoneChange}
              isLoading={loading}
              className="w-full"
              icon={Phone}
            >
              Enviar Código de Verificación
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <FormInput
              type="text"
              label="Código de verificación"
              placeholder="Ingresa el código de 6 dígitos"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              icon={KeyRound}
            />
            <FormInput
              type="tel"
              label="Nuevo número de teléfono"
              placeholder="Ej: 987654321"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              icon={Phone}
            />
            <Button
              onClick={handleVerifyPhoneCode}
              isLoading={loading}
              disabled={!verificationCode || !newValue}
              className="w-full"
              icon={Check}
            >
              Cambiar Número
            </Button>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          setVerificationStep('initial');
          setVerificationCode('');
          setNewValue('');
        }}
        title="Cambiar Correo Electrónico"
      >
        {verificationStep === 'initial' ? (
          <div className="space-y-6">
            <FormInput
              type="email"
              label="Nuevo correo electrónico"
              placeholder="nuevo@email.com"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              icon={Mail}
            />
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-blue-800 dark:text-blue-300 flex items-start">
                <Smartphone className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Te enviaremos un código de verificación por SMS al número <strong>{user.telefono}</strong>.</span>
              </p>
            </div>
            <Button
              onClick={handleEmailChange}
              isLoading={loading}
              className="w-full"
              disabled={!newValue || !newValue.includes('@')}
              icon={Mail}
            >
              Enviar Código SMS
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <FormInput
              type="text"
              label="Código de verificación SMS"
              placeholder="Ingresa el código de 6 dígitos"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerificationCode(value);
              }}
              icon={Smartphone}
            />
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <p className="text-blue-800 dark:text-blue-300 flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>Verifica el SMS enviado a tu teléfono <strong>{user.telefono}</strong> e ingresa el código de 6 dígitos.</span>
              </p>
            </div>
            {timeLeft > 0 && (
              <div className="text-center">
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  Tiempo restante: <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                  </span>
                </p>
              </div>
            )}
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
              <p className="text-amber-800 dark:text-amber-300 text-sm">
                Se actualizará tu correo de <strong>{user.email}</strong> a <strong>{newValue}</strong>.
              </p>
            </div>
            <Button
              onClick={handleVerifyEmailCode}
              isLoading={loading}
              disabled={!verificationCode || verificationCode.length !== 6}
              className="w-full"
              icon={Check}
            >
              Cambiar Email
            </Button>
            {verificationStep === 'verify' && timeLeft <= 0 && (
              <Button
                onClick={handleEmailChange}
                variant="secondary"
                className="w-full"
                disabled={loading}
                icon={RefreshCw}
              >
                Reenviar Código
              </Button>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isThemeModalOpen}
        onClose={() => setIsThemeModalOpen(false)}
        title="Personalizar Apariencia"
      >
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 p-3 rounded-2xl inline-flex mb-3">
              <Palette className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              Selecciona el tema que prefieras para personalizar la interfaz
            </p>
          </div>
          
          <ThemeSelector
            currentTheme={selectedTheme}
            onThemeChange={handleThemeChange}
          />
          
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              El modo "Sistema" detecta automáticamente las preferencias de tu dispositivo
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPreferencesModalOpen}
        onClose={() => setIsPreferencesModalOpen(false)}
        title="Preferencias de Búsqueda"
        size="lg"
      >
        <div className="space-y-8">
          {/* Tipos de Propiedad */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              Tipos de Propiedad Preferidos
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                { id: 'casa', label: 'Casa', icon: Home },
                { id: 'departamento', label: 'Departamento', icon: Building2 },
                { id: 'terreno', label: 'Terreno', icon: MapPin }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handlePropertyTypeToggle(id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 border-2
                          ${preferences.tipo_propiedad.includes(id)
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-transparent shadow-lg'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Rango de Precios */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              type="number"
              label="Presupuesto Mínimo (S/)"
              placeholder="100,000"
              value={preferences.presupuesto_min}
              onChange={(e) => setPreferences(prev => ({
                ...prev,
                presupuesto_min: e.target.value
              }))}
            />
            <FormInput
              type="number"
              label="Presupuesto Máximo (S/)"
              placeholder="500,000"
              value={preferences.presupuesto_max}
              onChange={(e) => setPreferences(prev => ({
                ...prev,
                presupuesto_max: e.target.value
              }))}
            />
          </div>

          {/* Características de la Propiedad */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Dormitorios
              </label>
              <select
                name="dormitorios"
                value={preferences.dormitorios}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  dormitorios: e.target.value
                }))}
                className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                        focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                        dark:bg-gray-700 dark:text-gray-300"
              >
                <option value="">Sin preferencia</option>
                {[1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num}+</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Baños
              </label>
              <select
                name="banos"
                value={preferences.banos}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  banos: e.target.value
                }))}
                className="w-full p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                        focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                        dark:bg-gray-700 dark:text-gray-300"
              >
                <option value="">Sin preferencia</option>
                {[1, 2, 3, 4].map(num => (
                  <option key={num} value={num}>{num}+</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Área (m²)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="area_min"
                  value={preferences.area_min}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    area_min: e.target.value
                  }))}
                  placeholder="Min"
                  className="w-1/2 p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                          focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                          dark:bg-gray-700 dark:text-gray-300"
                />
                <input
                  type="number"
                  name="area_max"
                  value={preferences.area_max}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    area_max: e.target.value
                  }))}
                  placeholder="Max"
                  className="w-1/2 p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                          focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                          dark:bg-gray-700 dark:text-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setIsPreferencesModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handlePreferencesChange}
              isLoading={loading}
              icon={Save}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Detalles de Suscripción */}
      <Modal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        title="Detalles de tu Plan"
        size="lg"
      >
        {planLoading ? (
          <div className="py-12 text-center">
            <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-6"></div>
            <p className="text-gray-600 dark:text-gray-400">Cargando información del plan...</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Encabezado del Plan */}
            <div className="text-center">
              <div className={`inline-flex items-center justify-center p-4 rounded-2xl bg-gradient-to-r ${
                planInfo?.plan === 'premium' ? 'from-amber-400 to-orange-500' :
                planInfo?.plan === 'estandar' ? 'from-purple-500 to-pink-600' :
                planInfo?.plan === 'basico' ? 'from-green-500 to-emerald-600' :
                planInfo?.plan === 'casual' ? 'from-blue-500 to-purple-600' :
                'from-gray-400 to-gray-600'
              } mb-4`}>
                <PaymentCard className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                Plan {planNames[planInfo?.plan || 'gratuito']}
              </h2>
            </div>

            {/* Detalles del Plan */}
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-2xl space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <Activity className="w-4 h-4" />
                  Publicaciones:
                </span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {planInfo?.total_propiedades || 0} / {
                    planInfo?.plan === 'premium' ? '∞' : 
                    planInfo?.plan === 'estandar' ? '20' : 
                    planInfo?.plan === 'basico' ? '10' : 
                    planInfo?.plan === 'casual' ? '5' : '1'
                  }
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Ciclo de facturación:
                </span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {planInfo?.ciclo_facturacion === 'anual' ? 'Anual' : 'Mensual'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <Star className="w-4 h-4" />
                  Fecha de inicio:
                </span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {planInfo?.fecha_inicio
                    ? new Date(planInfo.fecha_inicio).toLocaleDateString('es-PE', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'No disponible'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Próximo cobro:
                </span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {planInfo?.fecha_proximo_pago
                    ? new Date(planInfo.fecha_proximo_pago).toLocaleDateString('es-PE', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })
                    : 'No disponible'}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Estado de renovación:
                </span>
                <span className={`font-semibold flex items-center gap-1 ${
                  planInfo?.renovacion_automatica === 'si' 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-amber-600 dark:text-amber-400'
                }`}>
                  {planInfo?.renovacion_automatica === 'si' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  {planInfo?.renovacion_automatica === 'si' ? 'Activa' : 'Desactivada'}
                </span>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex flex-col gap-3">
              {planInfo?.plan !== 'gratuito' && (
                <>
                  {planInfo?.renovacion_automatica === 'si' && (
                    <Button
                      variant="danger"
                      icon={Ban}
                      onClick={handleCancelSubscription}
                      isLoading={cancelSubscriptionLoading}
                      className="w-full"
                    >
                      Cancelar Renovación Automática
                    </Button>
                  )}
                  
                  {planInfo?.renovacion_automatica === 'no' && (
                    <Button
                      variant="success"
                      icon={CheckCircle}
                      onClick={handleReactivateSubscription}
                      isLoading={reactivateSubscriptionLoading}
                      className="w-full"
                    >
                      Reactivar Renovación Automática
                    </Button>
                  )}
                </>
              )}
              
              <Button
                onClick={() => navigate('/upgrade-plan')}
                className="w-full"
                icon={TrendingUp}
              >
                Cambiar Plan
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal para añadir tarjeta */}
      <Modal
        isOpen={isAddCardModalOpen}
        onClose={() => setIsAddCardModalOpen(false)}
        title="Añadir Método de Pago"
        size="lg"
      >
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl mb-6">
            <p className="text-blue-800 dark:text-blue-300 flex items-start">
              <Shield className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>Tus datos de pago están seguros. Usamos PayU para procesar pagos de forma segura con encriptación de nivel bancario.</span>
            </p>
          </div>
          
          <FormInput
            type="text"
            label="Número de Tarjeta"
            placeholder="1234 5678 9012 3456"
            value={newCardData.cardNumber}
            onChange={handleCardNumberChange}
            icon={CreditCard}
          />
          
          <FormInput
            type="text"
            label="Titular de la Tarjeta"
            placeholder="NOMBRE DEL TITULAR"
            value={newCardData.cardHolder}
            onChange={(e) => setNewCardData({...newCardData, cardHolder: e.target.value.toUpperCase()})}
            icon={User}
          />
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Fecha de Expiración
              </label>
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={newCardData.expiryMonth}
                  onChange={(e) => setNewCardData({...newCardData, expiryMonth: e.target.value})}
                  className="p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                          focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                          dark:bg-gray-700 dark:text-gray-300"
                >
                  <option value="">MM</option>
                  {Array.from({length: 12}, (_, i) => {
                    const month = i + 1;
                    return (
                      <option key={month} value={month.toString().padStart(2, '0')}>
                        {month.toString().padStart(2, '0')}
                      </option>
                    );
                  })}
                </select>
                <select
                  value={newCardData.expiryYear}
                  onChange={(e) => setNewCardData({...newCardData, expiryYear: e.target.value})}
                  className="p-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl transition-all duration-200
                          focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500
                          dark:bg-gray-700 dark:text-gray-300"
                >
                  <option value="">AA</option>
                  {Array.from({length: 10}, (_, i) => {
                    const year = new Date().getFullYear() % 100 + i;
                    return (
                      <option key={year} value={year.toString().padStart(2, '0')}>
                        {year.toString().padStart(2, '0')}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            
            <FormInput
              type="password"
              label="CVV"
              placeholder="123"
              value={newCardData.cvv}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                setNewCardData({...newCardData, cvv: value});
              }}
              icon={LockKeyhole}
            />
          </div>
          
          <FormInput
            type="text"
            label="Documento de Identidad (opcional)"
            placeholder="DNI o RUC"
            value={newCardData.docNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setNewCardData({...newCardData, docNumber: value});
            }}
            icon={Shield}
          />
          
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => setIsAddCardModalOpen(false)}
              disabled={processingCard}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCard}
              isLoading={processingCard}
              disabled={!newCardData.cardNumber || !newCardData.cardHolder || 
                      !newCardData.expiryMonth || !newCardData.expiryYear || 
                      !newCardData.cvv}
              icon={Save}
            >
              Guardar Tarjeta
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal para confirmar eliminación de tarjeta */}
      <Modal
        isOpen={isDeleteCardModalOpen}
        onClose={() => {
          setIsDeleteCardModalOpen(false);
          setSelectedCard(null);
        }}
        title="Eliminar Método de Pago"
      >
        <div className="space-y-6">
          <div className="p-4 border-2 border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-xl">
            <p className="text-red-600 dark:text-red-400 flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>
                ¿Estás seguro de que deseas eliminar esta tarjeta? 
                {selectedCard && (
                  <span className="font-semibold block mt-2">
                    Tarjeta: •••• {selectedCard.lastFour}
                  </span>
                )}
              </span>
            </p>
          </div>
          
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
            <p className="text-amber-700 dark:text-amber-300 text-sm flex items-start">
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>
                Esta acción no se puede deshacer. Si esta tarjeta está configurada para renovación automática, 
                deberás agregar otra tarjeta o tu suscripción podría ser cancelada.
              </span>
            </p>
          </div>
          
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteCardModalOpen(false);
                setSelectedCard(null);
              }}
              disabled={processingCard}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteCard}
              isLoading={processingCard}
              icon={Trash2}
            >
              Eliminar Tarjeta
            </Button>
          </div>
        </div>
      </Modal>

      {/* Footer con información adicional */}
      <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-100 dark:border-blue-800">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-gray-100">Configuración Sincronizada</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tus preferencias se guardan automáticamente • Acceso desde cualquier dispositivo
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-gray-700 dark:text-gray-300">Datos Seguros</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-gray-700 dark:text-gray-300">SSL Encriptado</span>
            </div>
          </div>
        </div>
      </div>

      {/* Styles para animaciones */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out forwards;
        }

        /* Smooth scrolling para pestañas */
        .overflow-x-auto::-webkit-scrollbar {
          height: 4px;
        }

        .overflow-x-auto::-webkit-scrollbar-track {
          background: #f1f5f9;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }

        .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Dark mode scrollbar */
        .dark .overflow-x-auto::-webkit-scrollbar-track {
          background: #374151;
        }

        .dark .overflow-x-auto::-webkit-scrollbar-thumb {
          background: #6b7280;
        }

        .dark .overflow-x-auto::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default Configuracion;